import ROLE from "models/role";
import { matchPath } from "react-router";
import itAdminSidebarItems from "./itAdmin";
import managerSidebarItems from "./manager";
import peopleTeamSidebarItems from "./peopleTeam";
import teammateSidebarItems from "./teammate";
import { SideBarItem, SidebarSection } from "./shared";
import newHireDashboardItems from "./newHire";

function calculatePathSpecificity(matchedPath: string): number {
  if (!matchedPath) return 0;

  // Split path into segments, removing empty strings
  const segments = matchedPath.split("/").filter((segment) => segment.length > 0);

  let specificity = 0;

  // Base score: number of segments (each segment adds 100 points)
  specificity += segments.length * 100;

  // Analyze each segment
  segments.forEach((segment) => {
    // Static segments get higher priority than dynamic ones
    if (segment.startsWith(":") || segment.includes(":")) {
      // Dynamic segment (like :id) - lower priority
      specificity += 10;
    } else {
      // Static segment - higher priority
      specificity += 50;
    }

    // Longer segments get slightly higher priority
    specificity += Math.min(segment.length, 20);
  });

  return specificity;
}

function getAllMatchedPaths(item: SideBarItem, pathname: string): string[] {
  const matchedPaths: string[] = [];

  // Check main route using matchPath
  const isExactMatch = item.routeMatchExact || false;
  const mainMatch = matchPath({ path: item.route, end: isExactMatch }, pathname);
  if (mainMatch) {
    matchedPaths.push(mainMatch.pathname);
  }

  // Check additional routes using matchPath with their own exactMatch config
  item.additionalRoutes?.forEach((routeConfig) => {
    const match = matchPath({ path: routeConfig.path, end: routeConfig.exactMatch || false }, pathname);
    if (match) {
      matchedPaths.push(match.pathname);
    }
  });

  return matchedPaths;
}

export function findMostSpecificSidebarItem(pathname: string, sidebarSections: SidebarSection[]): string | null {
  let bestMatch: { id: string; specificity: number } | undefined;

  sidebarSections.forEach((section) => {
    section.items.forEach((item) => {
      // Use centralized route matching logic
      const matchedPaths = getAllMatchedPaths(item, pathname);

      if (matchedPaths.length > 0) {
        // Calculate specificity for all matched paths and find the maximum
        const maxSpecificity = Math.max(...matchedPaths.map((path) => calculatePathSpecificity(path)));

        if (!bestMatch || maxSpecificity > bestMatch.specificity) {
          bestMatch = { id: item.id, specificity: maxSpecificity };
        }
      }
    });
  });

  return bestMatch?.id ?? null;
}

export default function sideBarItemsByRole(role: ROLE): SidebarSection[] {
  switch (role) {
    case ROLE.COMPANY_ADMIN:
    case ROLE.PEOPLE_TEAM:
      return peopleTeamSidebarItems;
    case ROLE.BASIC:
      return teammateSidebarItems;
    case ROLE.MANAGER:
      return managerSidebarItems;
    case ROLE.ONBOARDER:
      return newHireDashboardItems;
    case ROLE.IT_ADMIN:
      return itAdminSidebarItems;
    case ROLE.CAMINO_TEAM:
      return [];
    default:
      return [];
  }
}

/**
 * Merge sidebar sections from each role the user holds.
 *
 * Sections are keyed by `id`. The first occurrence of a section wins on order
 * and header; subsequent occurrences contribute any items not already present
 * (deduped by item `id`).
 *
 * Example: a user with [MANAGER, IT_ADMIN] gets the Manager "top" section AND
 * the IT-Admin "company" section with Integrations — no duplication.
 */
export function sidebarItemsForRoles(roles: ROLE[]): SidebarSection[] {
  const merged = new Map<string, SidebarSection>();

  roles.forEach((role) => {
    sideBarItemsByRole(role).forEach((section) => {
      const existing = merged.get(section.id);
      if (!existing) {
        merged.set(section.id, { ...section, items: [...section.items] });
        return;
      }
      const seenItemIds = new Set(existing.items.map((item) => item.id));
      section.items.forEach((item) => {
        if (!seenItemIds.has(item.id)) {
          existing.items.push(item);
          seenItemIds.add(item.id);
        }
      });
      if (!existing.header && section.header) existing.header = section.header;
    });
  });

  return Array.from(merged.values());
}

// New-hire portal is URL-gated and stands alone — never merged with role sections.
export function sidebarItemsFor(pathname: string, permissions: ROLE[]): SidebarSection[] {
  if (pathname.startsWith("/my-onboarding")) return newHireDashboardItems;
  return sidebarItemsForRoles(permissions.filter((role) => role !== ROLE.ONBOARDER));
}
